import "./Modals.scss";
import { useCallback, useEffect, useState } from "react";
import { Button, Modal, Table, Typography } from "antd";
import { LeaveRequest, ModalProps } from "../../utils/types";
import {
  fetchData,
  fixedTableScrollY,
  formatEnumLabel,
  getRequestStatusType,
  Notify,
} from "../../utils/constants";
import { ColumnsType } from "antd/es/table";

type RequestsModalProps = ModalProps & {
  type: "Approved" | "Rejected" | "Cancelled" | "Pending" | "PendingCancelled";
  userId: number | undefined;
};

const filterStatusMap: Record<string, string> = {
  Approved: "APPROVED",
  Rejected: "REJECTED",
  Cancelled: "CANCELLED",
  Pending: "AWAITING_MANAGER",
  PendingCancelled: "AWAITING_MANAGER_CANCELLATION",
};

const RequestsModal = ({
  isOpen,
  setIsOpen,
  type,
  userId,
}: RequestsModalProps) => {
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchTableData = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/getRequestsByEmployeeId/${userId}`,
        setLeaveRequests,
      );
    } catch (error) {
      console.error("Error while fetching employee requests:", error);
      Notify("error", "Failed to fetch employee requests.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!isOpen || !userId) return;
    fetchTableData();
  }, [fetchTableData, isOpen, userId]);

  const filteredRequests = leaveRequests.filter(
    (lq) => lq.requestStatus === filterStatusMap[type],
  );

  const columns: ColumnsType<LeaveRequest> = [
    {
      title: "Request Type",
      dataIndex: "requestType",
      key: "requestType",
      render: (value: string) => formatEnumLabel(value),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
    },
    {
      title: "Substitute",
      dataIndex: "substituteName",
      key: "substituteName",
    },
    {
      title: "Status",
      dataIndex: "requestStatus",
      key: "requestStatus",
      render: (value: string) => (
        <span className={getRequestStatusType(value)}>
          {formatEnumLabel(value)}
        </span>
      ),
    },
  ];

  const approvedColumns: ColumnsType<LeaveRequest> = [
    ...columns,
    {
      title: "Approver",
      dataIndex: "approverName",
      key: "approverName",
      render: (value: string | undefined) => value || "-",
    },
    {
      title: "Number of approved days",
      key: "approvedDays",
      render: (_: unknown, record: LeaveRequest) =>
        record.requestedDays || record.leaveDays?.length || "-",
    },
  ];

  return (
    <Modal
      className="md"
      width="auto"
      open={isOpen}
      title={`${type} Requests`}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="close" onClick={() => setIsOpen(false)}>
          Close
        </Button>,
      ]}
    >
      <Typography.Paragraph />
      <Table
        className="view-table"
        columns={type === "Approved" ? approvedColumns : columns}
        dataSource={filteredRequests}
        scroll={{ y: fixedTableScrollY }}
        loading={{ spinning: loading, tip: "Loading leave requests..." }}
        pagination={false}
        rowKey={(record) => `${record.id}-${record.requestType}`}
        locale={{
          emptyText: `No ${type.toLowerCase()} leave requests found.`,
        }}
      />
    </Modal>
  );
};

export default RequestsModal;
